import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { checkLessonSources, listLessonSourceChecks } from '@platform/client';
import { lessonSourceChecksSchema } from '@platform/schema/verification';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';

import { useTrackedMutation } from '../actions/ActionsProvider';
import { describeApiError } from '../api-error';
import { reviewQuery } from '../library/sources';

import {
  LessonFixFlow,
  sourceFixFor,
  STATUS_LABEL,
  useLessonFixes,
} from './LessonFix';

import type { LessonFix } from '@platform/schema/lesson-fix';
import type {
  ClaimEvidence,
  LessonSourceChecks,
  SourceCheckStatus,
  SourceProposal,
} from '@platform/schema/verification';

/**
 * Each source a lesson cites, what Find / verify source read on it, and every claim held
 * against it with the passage nearest — CURR-37's records, shown for CURR-38. Admin
 * details only; nothing here reaches a learner.
 */

type Source = LessonSourceChecks['sources'][number];

const STATUS: Record<SourceCheckStatus, string> = {
  UNREACHABLE: 'Not reached',
  UNREADABLE: 'Reached, nothing readable',
  UNJUDGED: 'Read, not judged',
  CHECKED: 'Read and judged',
};

const VERDICT = {
  SUPPORTED: { label: 'Supported', colour: 'success' },
  UNSUPPORTED: { label: 'Unsupported', colour: 'error' },
  UNRESOLVED: { label: 'Unresolved', colour: 'warning' },
} as const;

const proposed = (proposal: SourceProposal): string => {
  switch (proposal.kind) {
    case 'REPAIR_CITATION':
      return `Cite ${proposal.cite.title} (${proposal.cite.url}), which states it. ${proposal.reason}`;
    case 'REPLACE_SOURCE':
      return `Find a source that states: ${proposal.needs}. ${proposal.reason}`;
    case 'REVISE_CLAIM':
      return `Revise the claim to what the source says: "${proposal.passage}". ${proposal.reason}`;
  }
};

const scenesOf = (claim: ClaimEvidence): string =>
  claim.scenes.length === 0
    ? 'No scene makes it now'
    : `Scene${claim.scenes.length === 1 ? '' : 's'} ${claim.scenes.join(', ')}`;

const sourceChecksQuery = (lessonId: string) => ({
  queryKey: ['review', lessonId, 'source-checks'],
  queryFn: async () =>
    lessonSourceChecksSchema.parse(
      (
        await listLessonSourceChecks({
          path: { id: lessonId },
          throwOnError: true,
        })
      ).data,
    ).sources,
});

/**
 * In the review an open claim is fixed here; without `review`, as in the lesson's details,
 * it links to the review.
 */
export function SourceChecks({
  lessonId,
  review = false,
}: {
  lessonId: string;
  review?: boolean;
}) {
  const sources = useQuery(sourceChecksQuery(lessonId));
  const fixes = useLessonFixes(lessonId);

  if (sources.isError)
    return <Alert severity="error">{describeApiError(sources.error)}</Alert>;
  if (sources.data === undefined) return null;

  return (
    <Stack spacing={1} role="region" aria-label="Sources and evidence">
      <Typography variant="subtitle2" component="h3">
        Sources and evidence
      </Typography>
      {sources.data.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          This lesson cites no source.
        </Typography>
      )}
      {sources.data.map((source) => (
        <SourceCard key={`${source.title}-${source.url ?? ''}`} source={source}>
          {source.check?.claims.map((claim) => (
            <Claim
              key={claim.claim}
              claim={claim}
              read={source.check?.status === 'CHECKED'}
            >
              {claim.verdict !== 'SUPPORTED' && source.url !== undefined && (
                <ClaimFix
                  lessonId={lessonId}
                  url={source.url}
                  claim={claim.claim}
                  fixes={fixes}
                  review={review}
                />
              )}
            </Claim>
          ))}
          {source.url !== undefined && (
            <FindSource lessonId={lessonId} body={{ url: source.url }} />
          )}
        </SourceCard>
      ))}
    </Stack>
  );
}

function SourceCard({
  source,
  children,
}: {
  source: Source;
  children: React.ReactNode;
}) {
  const check = source.check;
  return (
    <Stack
      component={Paper}
      variant="outlined"
      spacing={1}
      sx={{ p: 1 }}
      data-testid="source"
    >
      <Typography variant="body2">{source.title}</Typography>
      <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>
        {source.url ?? 'No address to open'} · {source.type}
      </Typography>
      {check === undefined ? (
        <Typography variant="body2" color="text.secondary">
          Not checked yet.
        </Typography>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {STATUS[check.status]} · {new Date(check.checkedAt).toLocaleString()}
          {check.current ? '' : ' · the lesson has changed since'}
          {check.failure === undefined ? '' : ` · ${check.failure}`}
        </Typography>
      )}
      {check?.advisories.map((line) => (
        <Typography key={line} variant="caption">
          Advisory: {line}
        </Typography>
      ))}
      {children}
    </Stack>
  );
}

function Claim({
  claim,
  read,
  children,
}: {
  claim: ClaimEvidence;
  read: boolean;
  children: React.ReactNode;
}) {
  const verdict = VERDICT[claim.verdict];
  return (
    <Stack spacing={0.5} sx={{ pl: 1 }} data-testid="claim">
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: 'center', flexWrap: 'wrap' }}
      >
        <Chip size="small" color={verdict.colour} label={verdict.label} />
        {claim.verdict !== 'SUPPORTED' && (
          <Chip
            size="small"
            variant="outlined"
            label={claim.blocking ? 'Blocking' : 'Advisory'}
          />
        )}
        <Typography variant="body2" color="text.secondary">
          {scenesOf(claim)}
        </Typography>
      </Stack>
      <Typography variant="body2">Claim: {claim.claim}</Typography>
      <Typography variant="body2" color="text.secondary">
        {claim.passage === undefined
          ? `Passage: none, ${read ? 'nothing on the page is near this claim' : 'the page was not read'}.`
          : `Passage: "${claim.passage}"`}
      </Typography>
      {claim.verdict !== 'SUPPORTED' && (
        <Typography variant="body2" color="text.secondary">
          Why it matters: {claim.reason}
        </Typography>
      )}
      {claim.proposal !== undefined && (
        <Typography variant="body2" color="text.secondary">
          Proposed change: {proposed(claim.proposal)}
        </Typography>
      )}
      {children}
    </Stack>
  );
}

function ClaimFix({
  lessonId,
  url,
  claim,
  fixes,
  review,
}: {
  lessonId: string;
  url: string;
  claim: string;
  fixes: readonly LessonFix[];
  review: boolean;
}) {
  const fix = sourceFixFor(fixes, url, claim);
  const status = (
    <Chip size="small" label={STATUS_LABEL[fix?.status ?? 'DISCARDED']} />
  );
  if (review)
    return (
      <Stack spacing={1} sx={{ alignItems: 'flex-start' }}>
        {status}
        <LessonFixFlow
          lessonId={lessonId}
          fix={fix}
          request={{ source: { url, claim } }}
        />
      </Stack>
    );

  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
      {status}
      <Button
        size="small"
        variant="outlined"
        component={RouterLink}
        to={`/authoring/lessons/${lessonId}`}
      >
        Fix in review
      </Button>
    </Stack>
  );
}

/**
 * Find / verify source: one source for the latest run's claims, or every source for one
 * claim. The result lands in Sources and evidence.
 */
export function FindSource({
  lessonId,
  body,
  compact = false,
}: {
  lessonId: string;
  body: { url: string } | { claim: string };
  compact?: boolean;
}) {
  const queries = useQueryClient();
  const check = useTrackedMutation('Find / verify source', {
    mutationFn: async () => {
      await checkLessonSources({
        path: { id: lessonId },
        body,
        throwOnError: true,
      });
    },
    onSuccess: () =>
      queries.invalidateQueries({ queryKey: reviewQuery(lessonId).queryKey }),
  });

  return (
    <Stack spacing={0.5} component={compact ? 'span' : 'div'}>
      <Stack direction="row" component="span">
        <Button
          size="small"
          variant={compact ? 'text' : 'outlined'}
          color={compact ? 'inherit' : 'primary'}
          disabled={check.isPending}
          onClick={() => {
            check.mutate();
          }}
        >
          Find / verify source
        </Button>
      </Stack>
      {check.isPending && <LinearProgress />}
      {check.isError && (
        <Alert severity="error">{describeApiError(check.error)}</Alert>
      )}
    </Stack>
  );
}
